// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, AppFooter, useCurrentUser, apiErrorMessage } from "./common";
import { NaverMapCanvas } from "../components/naver/NaverMapCanvas";

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.max(1, Math.round(ms / 60000));
  return min < 60 ? `${min}분 전` : `${Math.round(min/60)}시간 전`;
}

// Navigate to a region's dashboard view. Centralized so all drilldown
// entry points (row click, map pin) stay in sync.
function goToRegion(code) {
  if (!code) return;
  window.location.href = `/dashboard/region?code=${encodeURIComponent(code)}`;
}

// Period selector options. Order matters: rendered left→right and
// the last entry is the default (matches backend DEFAULT_PERIOD).
const PERIODS = [
  { key: "day",     label: "24시간" },
  { key: "week",    label: "7일" },
  { key: "month",   label: "30일" },
  { key: "quarter", label: "이번 분기" },
];
const PERIOD_NOTIF_LABEL = { day: "/일", week: "/주", month: "/30일", quarter: "/분기" };

// Delta helpers — one source of truth for KPI card "delta" labels.
// `gain8w`   = 8주 누적 증가 (cumulative 메트릭용)
// `thisWeek` = 시리즈 마지막 칸 값 (per-week 메트릭용)
function gain8w(arr)   { return arr && arr.length ? (arr[arr.length-1] - arr[0]) : 0; }
function thisWeek(arr) { return arr && arr.length ? arr[arr.length-1] : 0; }

// Silent polling cadence — KPIs + audit log refresh without UI interruption.
// Platform response carries only an aggregate alert count (no per-alert
// payload), so we cannot do per-arrival toasts here like on the region
// dashboard. Refreshing the KPIs is the most useful signal we can give.
const POLL_INTERVAL_MS = 30_000;

// 권역별 운영 현황 — 가짜 SVG 실루엣을 실제 네이버 지도로 교체.
//  - 권역당 마커 1개(집계). 크기=이달 매칭 건수, 색=성공률(green≥93 / orange≥88 / rose),
//    라벨 클릭 → 해당 권역 대시보드(goToRegion). 기존 SVG 핀과 동일한 시각 규칙 유지.
//  - 조작 잠금(interactive=false): 전국 분포를 한눈에 보는 용도. 본격 조작은 권역
//    상세에서. lazy(IntersectionObserver): 카드 진입 시에만 지도 인스턴스 생성.
//  - center_lat/lng 가 없는 권역은 마커를 건너뛰고, 좌표가 하나도 없으면 안내 폴백.
function PlatformRegionsMap({ regions }) {
  const wrapRef = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  const [map, setMap] = React.useState(null);

  React.useEffect(() => {
    if (inView) return;
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  // 좌표 있는 권역만 마커 대상. 전국 bbox는 이들로부터 계산.
  const located = (regions || []).filter(r => r.center_lat != null && r.center_lng != null);
  const bounds = React.useMemo(() => {
    if (!located.length) return null;
    const lats = located.map(r => r.center_lat), lngs = located.map(r => r.center_lng);
    const pad = 0.15; // 전국 스케일이라 마커가 가장자리에 붙지 않게 약간 여유.
    return {
      north: Math.max(...lats) + pad, south: Math.min(...lats) - pad,
      east:  Math.max(...lngs) + pad, west:  Math.min(...lngs) - pad,
    };
  }, [located]);
  const center = bounds
    ? { lat: (bounds.north + bounds.south) / 2, lng: (bounds.east + bounds.west) / 2 }
    : null;

  // 좌표/색/크기 시그니처가 바뀔 때만 마커 재생성(30초 폴링 깜빡임 방지).
  const sig = React.useMemo(
    () => JSON.stringify(located.map(r => [r.code, r.center_lat, r.center_lng, r.monthly_matches, r.success_rate])),
    [located]
  );

  React.useEffect(() => {
    if (!map || !window.naver?.maps) return;
    const naver = window.naver;
    const markers = located.map((r) => {
      const c  = r.success_rate >= 93 ? 'green' : r.success_rate >= 88 ? 'orange' : 'rose';
      const sz = r.monthly_matches >= 300 ? 38 : r.monthly_matches >= 200 ? 32 : r.monthly_matches >= 100 ? 26 : 20;
      const fs = sz > 26 ? 11 : 10;
      const name = (r.name || '').replace('권역', '');
      const marker = new naver.maps.Marker({
        map,
        position: new naver.maps.LatLng(r.center_lat, r.center_lng),
        title: `${r.name} · 매칭 ${r.monthly_matches} · 성공률 ${Math.round(r.success_rate)}%`,
        icon: {
          content: `<div style="display:flex;flex-direction:column;align-items:center;cursor:pointer;">`
            + `<div style="width:${sz}px;height:${sz}px;border-radius:50%;background:var(--${c}-500);color:#fff;font-weight:800;font-size:${fs}px;display:grid;place-items:center;box-shadow:0 3px 10px rgba(0,0,0,0.25);border:2px solid #fff;">${r.monthly_matches}</div>`
            + `<div style="font-size:9px;font-weight:700;color:var(--ink-1);margin-top:2px;white-space:nowrap;text-shadow:0 1px 3px rgba(255,255,255,0.95);">${name}</div>`
            + `</div>`,
          anchor: new naver.maps.Point(sz / 2, sz / 2),
        },
        zIndex: Math.round(r.monthly_matches), // 큰 권역이 위로(겹침 시 가독성).
      });
      naver.maps.Event.addListener(marker, "click", () => { goToRegion(r.code); });
      return marker;
    });
    return () => { markers.forEach(m => { try { m.setMap(null); } catch {} }); };
  }, [map, sig]);

  return (
    <div ref={wrapRef}
      style={{height:340, background:'var(--bg-sub)', position:'relative', overflow:'hidden'}}>
      {!center
        ? <div style={{position:'absolute', inset:0, display:'grid', placeItems:'center'}}>
            <span className="muted" style={{fontSize:12, fontWeight:600}}>권역 좌표가 설정되지 않았습니다.</span>
          </div>
        : inView
          ? <NaverMapCanvas center={center} bbox={bounds} zoom={7} interactive={false} fitPadding={36} onMapReady={setMap}/>
          : <div style={{position:'absolute', inset:0, display:'grid', placeItems:'center'}}>
              <span className="muted" style={{fontSize:12, fontWeight:600, background:'rgba(255,255,255,0.85)', padding:'6px 12px', borderRadius:8}}>지도 준비 중…</span>
            </div>}
    </div>
  );
}

function DashboardPlatform() {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [period, setPeriod] = React.useState("quarter");
  // Bumping this counter forces a refetch (used by visibility-change polling).
  const [refreshTick, setRefreshTick] = React.useState(0);
  // 시스템 관리자 매뉴얼(/manual/system) 버튼은 system_admin 에게만 노출한다.
  const { user } = useCurrentUser();

  React.useEffect(() => {
    fetch(`/api/v1/dashboard/platform?period=${period}`)
      .then(r => r.json())
      .then(setData)
      .catch(e => setErr(apiErrorMessage(e)));
  }, [period, refreshTick]);

  // 30s background polling. Pauses on hidden tab; immediately refetches on
  // foreground return so the system admin sees fresh state after a switch.
  React.useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") setRefreshTick(t => t + 1);
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  if (err)   return <div className="app" style={{padding:40, color:'var(--rose-500)'}}>API 오류: {err}</div>;
  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;

  const t = data.totals;
  const h = data.system_health;
  const ranked = [...data.regions].sort((a,b)=> b.monthly_matches - a.monthly_matches);

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="platform" active="dashboard"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="플랫폼 운영실" sub={`전국 ${t.regions}개 권역 · ${new Date().toLocaleDateString('ko-KR', {dateStyle:'long'})}`}>
            {user?.role === 'system_admin' && (
              <a href="/manual/system" title="시스템 관리자 사용자 매뉴얼"
                 style={{display:'inline-flex', alignItems:'center', gap:6, padding:'8px 12px', borderRadius:9, border:'1px solid var(--border-soft)', background:'var(--surface)', color:'var(--ink-1)', fontSize:12.5, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap'}}>
                <span style={{display:'inline-flex', width:16, height:16, color:'var(--orange-600)'}}>{Ic.help}</span>
                사용자 매뉴얼
              </a>
            )}
          </Topbar>
          <div className="main">

            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:18}}>
              <div>
                <div className="h1">전국 통합돌봄 현황</div>
                <div className="muted" style={{marginTop:4, fontSize:13}}>활성 권역 {t.regions.toLocaleString()}곳 · 등록 매니저 {t.managers.toLocaleString()}명 · 누적 케이스 {t.cases.toLocaleString()}건</div>
              </div>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <SystemHealthBadge status={h.status}/>
                <span className="muted" style={{fontSize:11}}>업타임 {h.uptime_days}일</span>
                <PeriodSwitch value={period} onChange={setPeriod}/>
              </div>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:18}}>
              <Kpi label="활성 권역"   value={t.regions.toLocaleString()}           delta={`+${gain8w(data.sparklines?.active_regions)} (8주)`}     deltaDir="up" sparkline={data.sparklines?.active_regions}/>
              <Kpi label="등록 매니저" value={t.managers.toLocaleString()}          delta={`+${gain8w(data.sparklines?.total_managers)} (8주)`}    deltaDir="up" sparkline={data.sparklines?.total_managers} color="var(--green-500)"/>
              <Kpi label="이달 매칭"   value={t.monthly_matches.toLocaleString()}   delta={`+${gain8w(data.sparklines?.monthly_matches)}건 (8주)`}  deltaDir="up" sparkline={data.sparklines?.monthly_matches}/>
              <Kpi label="누적 케이스" value={t.cases.toLocaleString()}             delta={`+${gain8w(data.sparklines?.total_cases)}건 (8주)`}      deltaDir="up" sparkline={data.sparklines?.total_cases} color="var(--green-500)"/>
              <Kpi label="긴급 알람"   value={t.alerts_critical.toLocaleString()}   delta={`+${thisWeek(data.sparklines?.alerts_critical)}건 (이번 주)`} deltaDir="up" sparkline={data.sparklines?.alerts_critical} color="var(--rose-500)"/>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1.3fr 1fr', gap:14, marginBottom:14}}>
              <div className="card" style={{overflow:'hidden'}}>
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 12px'}}>
                  <div className="h3">권역별 운영 현황</div>
                  <div style={{display:'flex', gap:6}}>
                    <span className="badge badge-orange">매칭 대상</span>
                    <span className="badge badge-green">성공률</span>
                  </div>
                </div>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:0}}>
                  <PlatformRegionsMap regions={data.regions}/>
                  <div style={{padding:'4px 18px 16px', display:'flex', flexDirection:'column'}}>
                    {ranked.slice(0, 6).map((r, i)=>(
                      <div key={r.id} onClick={()=>goToRegion(r.code)} title={`${r.name} 상세 대시보드 보기`}
                        style={{display:'flex', alignItems:'center', gap:10, padding:'10px 4px', borderTop: i>0 ? '1px solid var(--border-soft)' : 'none', cursor:'pointer', borderRadius:6}}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-sub)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{width:18, fontSize:11, fontWeight:700, color:'var(--ink-3)'}}>{i+1}</div>
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{fontSize:12.5, fontWeight:600}}>{r.name}</div>
                          <div className="muted mono" style={{fontSize:10}}>매칭 {r.monthly_matches} · 성공률 {r.success_rate.toFixed(0)}%</div>
                        </div>
                        {i===0 && <span className="badge badge-orange">성공률 1위</span>}
                        {r.grade === 'needs_improvement' && <span className="badge badge-amber">개선필요</span>}
                        <div className="bar" style={{width:60}}><span style={{width:`${r.success_rate}%`, background: r.success_rate>=93?'var(--green-500)':r.success_rate>=88?'var(--orange-500)':'var(--rose-500)'}}/></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card">
                <div style={{padding:'18px 22px 0'}}><div className="h3">시스템 헬스</div></div>
                <div style={{padding:'18px 22px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
                  {[
                    {l:'API p95',     v:`${h.api_p95_ms}ms`,                                   c: h.api_p95_ms < 300 ? 'green' : 'amber'},
                    {l:'Queue Lag',   v:`${h.queue_lag_s}s`,                                   c: h.queue_lag_s   < 5   ? 'green' : 'orange'},
                    {l:'Solid Queue', v: h.queue_status || '정상',                              c: h.queue_status === '정상' ? 'green' : 'rose'},
                    {l:'DB 크기',     v:`${h.db_size_mb}MB`,                                   c:'green'},
                    {l:'알림 발송',   v:`${(h.notifications_in_period || 0).toLocaleString()}${PERIOD_NOTIF_LABEL[period] || ''}`,  c:'sky'},
                    {l:'스토리지',    v:`${h.storage_percent || 0}%`,                          c: (h.storage_percent || 0) < 80 ? 'green' : 'amber'},
                  ].map((m,i)=>(
                    <div key={i} style={{padding:'12px 14px', background:'var(--bg-sub)', borderRadius:10}}>
                      <div className="muted" style={{fontSize:10, fontWeight:600}}>{m.l}</div>
                      <div style={{fontSize:17, fontWeight:800, color:`var(--${m.c}-${m.c==='amber'?'500':'600'})`, marginTop:2}}>{m.v}</div>
                    </div>
                  ))}
                </div>
                <div style={{borderTop:'1px solid var(--border-soft)', padding:'14px 22px'}}>
                  <div className="muted" style={{fontSize:11, marginBottom:8}}>지난 24시간 트래픽</div>
                  {data.traffic_24h?.length
                    ? <LineChart data={data.traffic_24h} height={56} color="var(--sky-500)" fill="var(--sky-50)"/>
                    : <div className="muted" style={{height:56, display:'grid', placeItems:'center', fontSize:11, background:'var(--bg-sub)', borderRadius:8}}>트래픽 데이터 없음</div>}
                </div>
              </div>
            </div>

            <div className="card">
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 0'}}>
                <div>
                  <div className="h3">최근 감사 로그</div>
                  <div className="muted" style={{fontSize:11, marginTop:2}}>최근 {PERIODS.find(p=>p.key===period)?.label || ''} 활동</div>
                </div>
                <a href="/audit" style={{fontSize:11, color:'var(--orange-600)', fontWeight:700, textDecoration:'none'}}>전체 감사 로그 →</a>
              </div>
              <table className="tbl" style={{marginTop:6}}>
                <thead><tr><th style={{paddingLeft:22}}>시각</th><th>주체</th><th>액션</th></tr></thead>
                <tbody>
                {data.audit_recent.map((row, i)=>(
                  <tr key={i} onClick={()=>{ window.location.href = `/audit?q=${encodeURIComponent(row.actor || '')}`; }}
                    title="클릭하여 감사 로그에서 보기"
                    style={{cursor:'pointer'}}>
                    <td style={{paddingLeft:22}} className="mono">{timeAgo(row.at)}</td>
                    <td>{row.actor}</td>
                    <td>{row.action}</td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}
// Segmented period selector. Keeps dashboard refetch scoped to one knob
// (the parent's `period` state) so future periods/labels are added in
// the PERIODS array only.
function PeriodSwitch({ value, onChange }) {
  return (
    <div role="tablist" aria-label="기간 선택"
      style={{display:'inline-flex', background:'var(--bg-sub)', borderRadius:10, padding:3, border:'1px solid var(--border-soft)'}}>
      {PERIODS.map(p => {
        const active = p.key === value;
        return (
          <button key={p.key} role="tab" aria-selected={active} onClick={()=>onChange(p.key)}
            style={{
              padding:'6px 12px', fontSize:12, fontWeight:700,
              border:'none', borderRadius:7, cursor:'pointer',
              background: active ? 'var(--surface)' : 'transparent',
              color:      active ? 'var(--ink-1)'   : 'var(--ink-3)',
              boxShadow:  active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              transition: 'background 0.15s, color 0.15s',
            }}>
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

// Maps backend status word ("정상"/"주의"/"위험") to badge color + label.
// Keep this dumb — all threshold logic lives in PlatformHealth on the server.
const STATUS_BADGE = {
  "정상": { color: "green", label: "시스템 정상" },
  "주의": { color: "amber", label: "주의" },
  "위험": { color: "rose",  label: "위험" },
};
function SystemHealthBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE["정상"];
  return (
    <span className={"badge badge-" + cfg.color}>
      <span className="dot" style={{background:`var(--${cfg.color}-500)`}}/> {cfg.label}
    </span>
  );
}

export { DashboardPlatform };
